import { useState } from 'react';
import {
  Box, Card, CardContent, TextField, Button, Typography, Alert,
  Grid, InputAdornment, IconButton, CircularProgress, Link,
} from '@mui/material';
import { Visibility, VisibilityOff, PersonAdd } from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL;

const Register = () => {
  const [fullName,   setFullName]   = useState('');
  const [email,      setEmail]      = useState('');
  const [password,   setPassword]   = useState('');
  const [confirm,    setConfirm]    = useState('');
  const [showPwd,    setShowPwd]    = useState(false);
  const [error,      setError]      = useState('');
  const [loading,    setLoading]    = useState(false);
  const navigate = useNavigate();

  const handleRegister = async () => {
    if (!email || !password) { setError('Email and password are required'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirm)  { setError('Passwords do not match'); return; }

    setLoading(true); setError('');
    try {
      const { data } = await axios.post(`${API}/api/auth/register`, { email, password, fullName });
      navigate('/login', { state: { info: data?.message || 'Account created. An administrator has been notified and will assign your role and school. You will receive an email once your access is ready.' } });
    } catch (err) {
      if (err.response?.data?.exists) {
        navigate('/login', { state: { info: 'An account with this email already exists. Please sign in.' } });
      } else {
        setError(err.response?.data?.error || 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => { if (e.key === 'Enter') handleRegister(); };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f4f6f8' }}>
      <Box sx={{ width: '100%', maxWidth: 420, px: 2 }}>
        <Card elevation={4} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
              <PersonAdd color="primary" />
              <Typography variant="h6" fontWeight={700}>Create your EduMIS account</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Your email address will be your username.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField label="Full Name" value={fullName} onChange={e => setFullName(e.target.value)}
                  onKeyDown={handleKey} fullWidth size="small" autoFocus placeholder="Optional" />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Email Address" type="email" value={email}
                  onChange={e => setEmail(e.target.value)} onKeyDown={handleKey}
                  fullWidth size="small" autoComplete="email" />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Password" type={showPwd ? 'text' : 'password'}
                  value={password} onChange={e => setPassword(e.target.value)}
                  onKeyDown={handleKey} fullWidth size="small"
                  helperText="Minimum 6 characters"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPwd(v => !v)} edge="end">
                          {showPwd ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Confirm Password" type={showPwd ? 'text' : 'password'}
                  value={confirm} onChange={e => setConfirm(e.target.value)}
                  onKeyDown={handleKey} fullWidth size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" fullWidth size="large"
                  onClick={handleRegister} disabled={loading}
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}>
                  {loading ? 'Creating account…' : 'Create Account'}
                </Button>
              </Grid>
            </Grid>

            <Typography variant="body2" color="text.secondary" align="center" mt={2.5}>
              Already have an account?{' '}
              <Link component="button" fontWeight={700} onClick={() => navigate('/login')}>
                Sign In
              </Link>
            </Typography>
          </CardContent>
        </Card>

        <Typography variant="caption" color="text.secondary" display="block" align="center" mt={2}>
          EduMIS by Dataport Technologies &nbsp;·&nbsp; EduMIS.dataportech.com
        </Typography>
      </Box>
    </Box>
  );
};

export default Register;
